import { useContext } from 'react';
import type { FC } from 'react';
import type { LocaldataProps } from 'react-localdata';
import { LocaldataContext } from 'react-localdata';
import classNames from 'classnames';

import OpponentAvatar from 'app/components/OpponentAvatar';
import { getTeamsWithNotDefined, isMatchFinished } from 'app/utils/match';
import type { MatchPreviewData, MatchPreviewTeam } from 'app/components/MatchPreview/types';

import DisplayedMarketOdds from './DisplayedMarketOdds';
import Winner3WayOdds from './Winner3WayOdds';
import MatchTeamsScore from './MatchTeamsScore';
import style from './style.scss';

interface Props {
  match: MatchPreviewData;
  forceDisplayedMarket?: MatchPreviewDisplayedMarket;
  compact?: boolean;
  teamAvatarSize?: number;
}

interface NotFinishedProps {
  match: MatchPreviewData;
  teams: [MatchPreviewTeam, MatchPreviewTeam];
  forceDisplayedMarket?: MatchPreviewDisplayedMarket;
  compact?: boolean;
}

const NotFinishedTeamsStat: FC<NotFinishedProps> = ({
  match,
  teams,
  forceDisplayedMarket = undefined,
  compact = undefined
}) => {
  const {
    localdata: { displayedMarketOnSchedule }
  }: LocaldataProps<AppLocaldata> = useContext(LocaldataContext);

  if (displayedMarketOnSchedule === 'winner' && !forceDisplayedMarket) {
    switch (match.winner_market.winner_market_template) {
      case 'winner-2-way':
        return <DisplayedMarketOdds match={match} teams={teams} valueClassName={style.teamValue} />;
      case 'winner-3-way':
        return <Winner3WayOdds match={match} teams={teams} />;
      default:
        return <DisplayedMarketOdds match={match} teams={teams} valueClassName={style.teamValue} />;
    }
  }
  return (
    <DisplayedMarketOdds
      match={match}
      teams={teams}
      valueClassName={style.teamValue}
      forceDisplayedMarket={forceDisplayedMarket}
      compact={compact}
    />
  );
};

NotFinishedTeamsStat.displayName = 'NotFinishedTeamsStat';

const MatchPreviewTeams: FC<Props> = ({
  match,
  forceDisplayedMarket = undefined,
  compact = undefined,
  teamAvatarSize = 25
}) => {
  const teams = getTeamsWithNotDefined(match.tournament.winner_type, match.opponents) as [
    MatchPreviewTeam,
    MatchPreviewTeam
  ];
  const [teamA, teamB] = teams;

  const displayTeams = !compact || forceDisplayedMarket !== 'map-over-under';

  return (
    <div className={style.teams}>
      {!compact && <span className={style.teamName}>{teamA.name}</span>}
      {displayTeams && <OpponentAvatar opponent={teamA} size={teamAvatarSize} />}
      <div className={classNames(style.values, { [style.compactValues]: compact })}>
        {isMatchFinished(match) ? (
          <MatchTeamsScore
            match={match}
            teams={teams}
            valueClassName={style.teamValue}
            compact={compact}
          />
        ) : (
          <NotFinishedTeamsStat
            match={match}
            teams={teams}
            forceDisplayedMarket={forceDisplayedMarket}
            compact={compact}
          />
        )}
      </div>
      {displayTeams && <OpponentAvatar opponent={teamB} size={teamAvatarSize} />}
      {!compact && <span className={style.teamName}>{teamB.name}</span>}
    </div>
  );
};

MatchPreviewTeams.displayName = 'MatchPreviewTeams';

export default MatchPreviewTeams;
